/**
 * 存为风格卡片弹窗：用户从图片节点提交风格。
 *
 * 预填图片的 prompt（可编辑），封面用原图。
 * 提交后进入待审核状态，管理员审核通过后上线。
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { platformFetch } from '../../../platform/http';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { toPersistableMediaUrl } from '../utils/persistableMediaUrl';
import toast from '../../../components/Toast';

interface StyleSubmitDialogProps {
  imageUrl: string;
  defaultPrompt: string;
  onClose: () => void;
}

export function StyleSubmitDialog({ imageUrl, defaultPrompt, onClose }: StyleSubmitDialogProps) {
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [category, setCategory] = useState('默认');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('请输入风格名称');
      return;
    }
    if (!prompt.trim()) {
      toast.error('提示词不能为空');
      return;
    }
    setSubmitting(true);
    try {
      // 封面用原图的 storage key（提交时传 persistable URL，后端存 key）
      const coverKey = toPersistableMediaUrl(imageUrl) ?? imageUrl;
      const res = await platformFetch('/canvas/style-cards/submit', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          prompt: prompt.trim(),
          category: category.trim() || '默认',
          coverStorageKey: coverKey,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '提交失败');
      }
      toast.success('已提交，等待管理员审核');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-[420px] max-w-[90vw] overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <span className="text-sm font-semibold text-slate-800">存为风格卡片</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {/* 封面预览 */}
          <div className="flex items-center gap-3">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-slate-200">
              <img
                src={resolveBrowserMediaUrl(imageUrl) || imageUrl}
                alt="封面预览"
                className="h-full w-full object-cover"
              />
            </div>
            <p className="text-xs text-slate-500">
              封面将使用当前图片，提交后进入待审核状态，管理员审核通过后上线。
            </p>
          </div>

          {/* 名称 */}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">风格名称</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="如：赛博朋克、水彩画风..."
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-fuchsia-300"
            />
          </div>

          {/* 分类 */}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">分类</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="默认"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-fuchsia-300"
            />
          </div>

          {/* 提示词 */}
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              提示词（可编辑，去掉个性化内容提炼为通用风格）
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-fuchsia-300"
            />
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-1.5 rounded-lg bg-fuchsia-500 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-fuchsia-600 disabled:opacity-40"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {submitting ? '提交中...' : '提交审核'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
